if (!customElements.get('tooto-card-carousel')) {
  class TootoCardCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryFrame = null;
      this.retryCount = 0;
      this.isBound = false;
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
    }

    connectedCallback() {
      if (!this.isBound) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
        this.addEventListener('click', this.handleClick);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        this.isBound = true;
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.isBound = false;
      this.destroy();
    }

    initialize() {
      if (this.swiper) return;

      const slider = this.querySelector('.tooto-card-carousel__slider');
      const slides = slider?.querySelectorAll('.swiper-wrapper > .tooto-card-carousel-item');

      if (!slider || !slides?.length) {
        this.classList.add('is-empty');
        return;
      }

      this.classList.remove('is-empty');

      if (slides.length === 1) {
        this.classList.add('is-static');
        return;
      }

      this.classList.remove('is-static');

      if (typeof window.Swiper !== 'function') {
        if (this.retryCount < 180) {
          this.retryCount += 1;
          this.retryFrame = requestAnimationFrame(() => this.initialize());
        }
        return;
      }

      this.retryCount = 0;

      const previousButton = this.querySelector('.tooto-card-carousel__button--previous');
      const nextButton = this.querySelector('.tooto-card-carousel__button--next');
      const mobileOffset = Number(this.dataset.offsetMobile) || 48;
      const desktopOffset = Number(this.dataset.offset) || 144;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion;
      const config = {
        loop: slides.length > 2,
        loopedSlides: Math.min(2, slides.length),
        effect: 'creative',
        speed: reducedMotion ? 0 : Number(this.dataset.speed) || 600,
        grabCursor: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.previousLabel,
          nextSlideMessage: this.dataset.nextLabel,
        },
        observer: true,
        observeParents: true,
        roundLengths: true,
        watchSlidesProgress: true,
        creativeEffect: {
          limitProgress: 1,
          prev: {
            shadow: true,
            translate: [-mobileOffset, 0, -120],
          },
          next: {
            shadow: true,
            translate: [mobileOffset, 0, -120],
          },
        },
        breakpoints: {
          750: {
            creativeEffect: {
              limitProgress: 1,
              prev: {
                shadow: true,
                translate: [-desktopOffset, 0, -120],
              },
              next: {
                shadow: true,
                translate: [desktopOffset, 0, -120],
              },
            },
          },
        },
      };

      if (previousButton && nextButton) {
        config.navigation = {
          prevEl: previousButton,
          nextEl: nextButton,
        };
      }

      if (autoplayEnabled) {
        config.autoplay = {
          delay: Number(this.dataset.autoplayDelay) || 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
      }

      this.swiper = new window.Swiper(slider, config);
    }

    destroy() {
      if (this.retryFrame) {
        cancelAnimationFrame(this.retryFrame);
        this.retryFrame = null;
      }

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }

      this.swiper = null;
      this.retryCount = 0;
    }

    handleBlockSelect(event) {
      const selectedItem = event.target.closest?.('.tooto-card-carousel-item');
      if (!selectedItem || !this.contains(selectedItem) || !this.swiper) return;

      const blockId = selectedItem.dataset.blockId;
      const originalSlides = Array.from(
        this.querySelectorAll('.swiper-wrapper > .tooto-card-carousel-item:not(.swiper-slide-duplicate)')
      );
      const slideIndex = originalSlides.findIndex((slide) => slide.dataset.blockId === blockId);

      if (slideIndex < 0) return;

      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(slideIndex, 300);
      } else {
        this.swiper.slideTo(slideIndex, 300);
      }

      this.stopAutoplay();
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.startAutoplay();
    }

    handleSectionUnload(event) {
      if (event.target.contains(this)) this.destroy();
    }

    handleClick(event) {
      if (this.dataset.designMode !== 'true') return;

      const link = event.target.closest?.('.tooto-card-carousel-item[href]');
      if (link && this.contains(link)) event.preventDefault();
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    }

    stopAutoplay() {
      if (this.swiper?.autoplay?.running) this.swiper.autoplay.stop();
    }

    startAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.swiper?.autoplay && !this.swiper.autoplay.running) this.swiper.autoplay.start();
    }
  }

  customElements.define('tooto-card-carousel', TootoCardCarousel);
}
